import { formatDateToDDMMYYYY } from "../../utils/dateUtils";
import React, { useEffect, useState } from 'react';
import axiosInstance from '../../config/axiosConfig';
import { MdVisibility, MdClose, MdSearch, MdDelete } from 'react-icons/md';
import { FaWhatsapp } from 'react-icons/fa';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const PressMediaList = () => {
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Search and Filter State
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  // Status mapping for display and values
  const statusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'selected', label: 'Selected' },
    { value: 'rejected', label: 'Rejected' },
    { value: 'not_response', label: 'Not Response' },
    { value: 'follow_up_needed', label: 'Follow Up Needed' },
  ];

  useEffect(() => {
    fetchApplications();
  }, []);

  const fetchApplications = async () => {
    try {
      const response = await axiosInstance.get('/forms/press-media-applications/');
      setApplications(response.data);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch applications. Please try again later.');
      setLoading(false);
    }
  };

  const handleStatusChange = async (appId, newStatus) => {
    try {
      // Optimistic UI update
      setApplications(prevApps => 
        prevApps.map(app => 
          app.id === appId ? { ...app, status: newStatus } : app
        )
      );

      await axiosInstance.patch(`/forms/press-media-applications/${appId}/`, {
        status: newStatus
      });
      
      toast.success('Status updated successfully');
    } catch (err) {
      console.error('Error updating status:', err);
      toast.error('Failed to update status');
      // Revert optimistic update
      fetchApplications(); 
    }
  };

  const handleDelete = async (appId) => {
    if (window.confirm("Are you sure you want to delete this application?")) {
      try {
        await axiosInstance.delete(`/forms/press-media-applications/${appId}/`);
        setApplications(prevApps => prevApps.filter(app => app.id !== appId));
        toast.success('Application deleted successfully');
      } catch (err) {
        console.error('Error deleting application:', err);
        toast.error('Failed to delete application');
      }
    }
  };

  const handleView = (app) => {
    setSelectedApp(app);
    setIsModalOpen(true);
    try {
      const viewedKey = 'viewed_form_ids';
      const viewed = JSON.parse(localStorage.getItem(viewedKey) || '[]');
      const formKey = `press_media_${app.id}`;
      if (!viewed.includes(formKey)) {
        viewed.push(formKey);
        localStorage.setItem(viewedKey, JSON.stringify(viewed));
        window.dispatchEvent(new Event('storage'));
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleWhatsAppShare = (app) => {
    const phoneNumber = "917200024453";
    const message = `*NAI Press Media Application Details*
--------------------------------
*Application No:* ${app.application_number || 'N/A'}
*Status:* ${statusOptions.find(o => o.value === app.status)?.label || app.status}
*Applied Date:* ${new Date(app.created_at).toLocaleDateString()}

*Personal Information*
*Name:* ${app.full_name}
*Father/Spouse Name:* ${app.father_spouse_name || '-'}
*DOB:* ${formatDateToDDMMYYYY(app.date_of_birth)}
*Gender:* ${app.gender}
*Email:* ${app.email}
*Mobile:* ${app.mobile}
*WhatsApp:* ${app.whatsapp || '-'}
*Address:* ${app.address || '-'}
*City:* ${app.city || '-'}
*State:* ${app.state || '-'}
*District:* ${app.district || '-'}
*Pin Code:* ${app.pin_code || '-'}

*Professional Information*
*Media House:* ${app.media_house_name}
*Designation:* ${app.designation}
*RNI Reg No:* ${app.rni_registration_no || '-'}
*Office Address:* ${app.office_address || '-'}

*Documents*
*Photo:* ${app.photograph || 'Not available'}
*ID Proof:* ${app.id_proof || 'Not available'}
    `.trim();
    
    const url = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedApp(null);
  };

  if (loading) return <div className="p-4 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div></div>;
  if (error) return <div className="p-4 text-red-500 text-center">{error}</div>;

  return (
    <div className="bg-white shadow rounded-lg overflow-hidden">
      <ToastContainer position="bottom-center" theme="dark" />
      <div className="px-4 py-5 sm:px-6 flex justify-between items-center border-b border-gray-200">
        <h3 className="text-lg leading-6 font-medium text-gray-900">
          Press Media Applications
        </h3>
        <span className="bg-blue-100 text-blue-800 text-xs font-semibold px-2.5 py-0.5 rounded">
          Total: {applications.length}
        </span>
      </div>

      {/* Search and Filter Section */}
      <div className="bg-gray-50 px-4 py-4 border-b border-gray-200 sm:px-6">
        <div className="flex flex-col md:flex-row gap-4 justify-between">
            <div className="relative flex-1 max-w-lg">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MdSearch className="h-5 w-5 text-gray-400" />
                </div>
                <input 
                    type="text" 
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50 sm:text-sm transition duration-150 ease-in-out" 
                    placeholder="Search by Name, State, or District"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                 />
            </div>
            <div className="flex flex-col md:flex-row gap-2">
                <div className="flex items-center space-x-2">
                    <label htmlFor="startDate" className="text-sm text-gray-600">From:</label>
                    <input 
                        id="startDate"
                        type="date" 
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                    />
                </div>
                <div className="flex items-center space-x-2">
                    <label htmlFor="endDate" className="text-sm text-gray-600">To:</label>
                    <input 
                        id="endDate"
                        type="date" 
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                    />
                </div>
                {(searchTerm || startDate || endDate) && (
                    <button 
                        onClick={() => { setSearchTerm(''); setStartDate(''); setEndDate(''); }}
                        className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md text-sm hover:bg-gray-300 focus:outline-none transition-colors"
                    >
                        Clear
                    </button>
                )}
            </div>
        </div>
      </div>
      
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Name
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                State
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                District
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Mobile No
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Applied At
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Remarks
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {/* Filter Logic */}
            {(() => {
                const filteredApplications = applications.filter(app => {
                    const matchesSearch = 
                    (app.full_name && app.full_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (app.state && app.state.toLowerCase().includes(searchTerm.toLowerCase())) ||
                    (app.district && app.district.toLowerCase().includes(searchTerm.toLowerCase()));
                    
                    let matchesDate = true;
                    if (startDate) {
                         // Compare start of the day
                        const filterStart = new Date(startDate);
                        filterStart.setHours(0, 0, 0, 0);
                        matchesDate = matchesDate && new Date(app.created_at) >= filterStart;
                    }
                    if (endDate) {
                        // Compare end of the day
                        const filterEnd = new Date(endDate);
                        filterEnd.setHours(23, 59, 59, 999);
                        matchesDate = matchesDate && new Date(app.created_at) <= filterEnd;
                    }
                    
                    return matchesSearch && matchesDate;
                });
            
                if (filteredApplications.length === 0) {
                    return (
                        <tr>
                            <td colSpan="7" className="px-6 py-4 text-center text-sm text-gray-500">
                                {applications.length > 0 ? "No results match your search." : "No applications found."}
                            </td>
                        </tr>
                    );
                }

                return filteredApplications.map((app) => (
                <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {app.full_name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {app.state}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {app.district}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {app.mobile}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(app.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                         <select
                            value={app.status || 'pending'}
                            onChange={(e) => handleStatusChange(app.id, e.target.value)}
                            className={`block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md 
                                ${app.status === 'selected' ? 'bg-green-50 text-green-700' : 
                                  app.status === 'rejected' ? 'bg-red-50 text-red-700' : 
                                  app.status === 'not_response' ? 'bg-yellow-50 text-yellow-700' :
                                  'bg-gray-50 text-gray-700'
                                }`}
                        >
                            {statusOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                        <button 
                            onClick={() => handleView(app)}
                            className="text-indigo-600 hover:text-indigo-900 flex items-center"
                            title="View Details"
                        >
                            <MdVisibility className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => handleWhatsAppShare(app)}
                            className="text-green-600 hover:text-green-900 flex items-center"
                            title="Share on WhatsApp"
                        >
                            <FaWhatsapp className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => handleDelete(app.id)}
                            className="text-red-600 hover:text-red-900 flex items-center"
                            title="Delete Application"
                        >
                            <MdDelete className="h-5 w-5" />
                        </button>
                    </div>
                    </td>
                </tr>
                ))
            })()}
          </tbody>
        </table>
      </div>

      {/* Detail Modal */}
      {isModalOpen && selectedApp && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={closeModal}></div>

            {/* Modal panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-start border-b pb-3">
                    <div>
                        <h3 className="text-xl leading-6 font-bold text-gray-900" id="modal-title">
                            Press Media Application: {selectedApp.application_number}
                        </h3>
                        <p className="text-xs text-gray-500 mt-1">
                            Applied On: {selectedApp.created_at ? new Date(selectedApp.created_at).toLocaleString() : '-'}
                        </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <span className={`px-3 py-1 text-xs font-bold rounded-full uppercase ${
                            selectedApp.status === 'selected' ? 'bg-green-100 text-green-800' :
                            selectedApp.status === 'rejected' ? 'bg-red-100 text-red-800' :
                            selectedApp.status === 'follow_up_needed' ? 'bg-orange-100 text-orange-800' :
                            selectedApp.status === 'not_response' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                        }`}>
                            {selectedApp.status || 'Pending'}
                        </span>
                        <button onClick={closeModal} className="text-gray-400 hover:text-gray-500 focus:outline-none p-1">
                            <MdClose className="h-6 w-6" />
                        </button>
                    </div>
                </div>
                
                <div className="mt-4 max-h-[70vh] overflow-y-auto pr-2">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-4">
                        
                        {/* Personal Info */}
                        <div className="col-span-1 md:col-span-2 bg-blue-50 p-2 rounded border border-blue-100">
                            <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Personal Information</h4>
                        </div>
                        <DetailItem label="Full Name" value={selectedApp.full_name} />
                        <DetailItem label="Father / Spouse Name" value={selectedApp.father_spouse_name} />
                        <DetailItem label="Date of Birth" value={formatDateToDDMMYYYY(selectedApp.date_of_birth)} />
                        <DetailItem label="Gender" value={selectedApp.gender} />
                        <DetailItem label="Marital Status" value={selectedApp.marital_status} />
                        <DetailItem label="Nationality" value={selectedApp.nationality} />
                        <DetailItem label="Email Address" value={selectedApp.email} />
                        <DetailItem label="Mobile Number" value={selectedApp.mobile} />
                        <DetailItem label="WhatsApp Number" value={selectedApp.whatsapp} />
                        <DetailItem label="Educational Qualification" value={selectedApp.education_qualification} />
                        <DetailItem label="Occupation" value={selectedApp.occupation} />
                        <DetailItem label="Nature of Business" value={selectedApp.nature_of_business} />
                        <DetailItem label="Proposer Name" value={selectedApp.proposer_name} />
                        <DetailItem label="Languages Known" value={formatLanguages(selectedApp.languages_known, selectedApp.other_language)} />

                        {/* Address & Location Info */}
                        <div className="col-span-1 md:col-span-2 bg-blue-50 p-2 rounded border border-blue-100 mt-2">
                            <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Residential & Location Details</h4>
                        </div>
                        <DetailItem label="Full Residential Address" value={selectedApp.address} />
                        <DetailItem label="City" value={selectedApp.city} />
                        <DetailItem label="State" value={selectedApp.state} />
                        <DetailItem label="District" value={selectedApp.district} />
                        <DetailItem label="Pin Code" value={selectedApp.pin_code} />
                        <DetailItem label="Area / Ward No" value={selectedApp.area_ward_no} />
                        <DetailItem label="Police Station" value={selectedApp.area_police_station} />
                        <DetailItem label="Taluk / Zone" value={selectedApp.taluk_or_zone} />
                        <DetailItem label="MLA Constituency" value={selectedApp.mla_constituency} />
                        <DetailItem label="MP Constituency" value={selectedApp.mp_constituency} />
                        
                        {/* Professional Info */}
                        <div className="col-span-1 md:col-span-2 bg-blue-50 p-2 rounded border border-blue-100 mt-2">
                            <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Professional & Media Information</h4>
                        </div>
                        <DetailItem label="Media House Name" value={selectedApp.media_house_name} />
                        <DetailItem label="Designation" value={selectedApp.designation} />
                        <DetailItem label="RNI Registration No" value={selectedApp.rni_registration_no} />
                        <DetailItem label="Proprietor Name" value={selectedApp.proprietor_name} />
                        <DetailItem label="Office Address" value={selectedApp.office_address} />
                        <DetailItem label="Office Location" value={`${selectedApp.office_city || ''}, ${selectedApp.office_state || ''} ${selectedApp.office_pin_code ? '- ' + selectedApp.office_pin_code : ''}`} />
                        <DetailItem label="Office Phone" value={selectedApp.office_phone} />
                        <DetailItem label="Office Email" value={selectedApp.office_email} />
                        <DetailItem label="Publication Frequency" value={selectedApp.frequency} />
                        <DetailItem label="Media Language" value={selectedApp.language} />
                        <DetailItem label="Chief Editor" value={selectedApp.chief_editor} />
                        <DetailItem label="Chief Editor Mobile" value={selectedApp.chief_editor_mobile} />
                        <DetailItem label="Website" value={selectedApp.website} />
                        <DetailItem label="Year of Establishment" value={selectedApp.year_of_establishment} />

                        {/* Declaration */}
                        <div className="col-span-1 md:col-span-2 bg-blue-50 p-2 rounded border border-blue-100 mt-2">
                            <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Declaration & Status</h4>
                        </div>
                        <DetailItem label="Declaration Accepted" value={selectedApp.declaration ? "Yes (Accepted terms)" : "No"} />
                        <DetailItem label="Current Application Status" value={(selectedApp.status || 'pending').toUpperCase()} />

                        {/* Uploaded Documents */}
                        <div className="col-span-1 md:col-span-2 bg-blue-50 p-2 rounded border border-blue-100 mt-2">
                            <h4 className="text-sm font-bold text-blue-900 uppercase tracking-wider">Uploaded Documents & Images</h4>
                        </div>
                        {selectedApp.photograph ? (
                            <div className="col-span-1 border p-2 rounded">
                                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Photograph</p>
                                <img src={selectedApp.photograph} alt="Photograph" className="h-36 w-auto object-cover border rounded shadow-sm" />
                            </div>
                        ) : (
                            <DetailItem label="Photograph" value="Not Uploaded" />
                        )}
                        {selectedApp.signature ? (
                            <div className="col-span-1 border p-2 rounded">
                                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Signature</p>
                                <img src={selectedApp.signature} alt="Signature" className="h-20 w-auto object-contain border rounded shadow-sm bg-gray-50" />
                            </div>
                        ) : (
                            <DetailItem label="Signature" value="Not Uploaded" />
                        )}
                        {selectedApp.id_proof ? (
                            <div className="col-span-1 border p-2 rounded">
                                <p className="text-xs font-bold text-gray-700 uppercase mb-1">ID Proof Document</p>
                                <a href={selectedApp.id_proof} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700">
                                    Open ID Proof
                                </a>
                            </div>
                        ) : (
                            <DetailItem label="ID Proof" value="Not Uploaded" />
                        )}
                        {selectedApp.address_proof ? (
                            <div className="col-span-1 border p-2 rounded">
                                <p className="text-xs font-bold text-gray-700 uppercase mb-1">Address Proof Document</p>
                                <a href={selectedApp.address_proof} target="_blank" rel="noopener noreferrer" className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-semibold hover:bg-blue-700">
                                    Open Address Proof
                                </a>
                            </div>
                        ) : (
                            <DetailItem label="Address Proof" value="Not Uploaded" />
                        )}

                    </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button 
                    type="button" 
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none sm:ml-3 sm:w-auto sm:text-sm"
                    onClick={closeModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const formatLanguages = (langs, otherLang) => {
  let list = [];
  if (langs) {
    if (typeof langs === 'object') {
      Object.entries(langs).forEach(([key, val]) => {
        if (val) list.push(key.charAt(0).toUpperCase() + key.slice(1));
      });
    } else if (typeof langs === 'string') {
      list.push(langs);
    }
  }
  if (otherLang) list.push(otherLang);
  return list.length > 0 ? list.join(', ') : '-';
};

const DetailItem = ({ label, value }) => (
    <div className="col-span-1 bg-gray-50 p-2 rounded border border-gray-100">
        <dt className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{label}</dt>
        <dd className="mt-0.5 text-sm font-bold text-gray-900 break-words">{value || '-'}</dd>
    </div>
);

export default PressMediaList;
